import React, { useRef, useState } from 'react';
import { tryUpload } from '../../services/photoStorage';
import './PhotoSlot.css';

const MAX_EDGE = 1600;

/* Phone photos are 3–12 MB; shrink each to a ~1600px JPEG before keeping it so a report
   full of photos stays light. One at a time — decoding ten photos in parallel crashes mobile Safari. */
const shrinkPhoto = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Couldn't read ${file.name}`));
    };
    img.src = url;
  });

/**
 * Capture / Gallery pair that actually keeps photos.
 * Capture opens the camera, Gallery opens the photo library (several at once).
 * `single` replaces the photo instead of adding (front of home).
 */
const PhotoSlot = ({ label, photos = [], onChange, single = false }) => {
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (e) => {
    const files = [...e.target.files];
    e.target.value = ''; // let the same photo be picked again later
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const file of files) {
      try {
        // shrunk on the phone, then online if there's signal (else it uploads before the report goes out)
        added.push(await tryUpload(await shrinkPhoto(file)));
      } catch (err) {
        console.error(err);
      }
    }
    setBusy(false);
    if (added.length) onChange(single ? added.slice(-1) : [...photos, ...added]);
  };

  return (
    <div className="photo-item">
      {label && <label>{label}</label>}
      {!single && photos.length > 0 && (
        <div className="photo-thumbs">
          {photos.map((src, i) => (
            <div className="photo-thumb" key={i}>
              <img src={src} alt="" />
              <button type="button" aria-label="Remove photo" onClick={() => onChange(photos.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="photo-buttons">
        <button type="button" className="btn-capture" disabled={busy} onClick={() => cameraInput.current.click()}>
          {busy ? 'Adding…' : '📷 Capture'}
        </button>
        <button type="button" className="btn-gallery" disabled={busy} onClick={() => galleryInput.current.click()}>
          🖼️ Gallery
        </button>
      </div>
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={handleFiles} />
      <input ref={galleryInput} type="file" accept="image/*" multiple={!single} hidden onChange={handleFiles} />
    </div>
  );
};

export default PhotoSlot;
